import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Home, BedDouble, Car, Waves, Zap, Wind, ThermometerSun, DoorClosed } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../context/LanguageContext';
const heroImages = ['https://cdn.magicpatterns.com/uploads/7u4YYy5PgooNiUw5cpsCMF/ChatGPT_Image_8_jun_2026,_16_20_20.png', 'https://cdn.magicpatterns.com/uploads/6zKXuFxw9GCeixwwJVePkB/ChatGPT_Image_8_jun_2026,_16_22_07.png', 'https://cdn.magicpatterns.com/uploads/sEaLj2yBJeYZphciqLxHfC/ChatGPT_Image_8_jun_2026,_16_23_53.png', 'https://cdn.magicpatterns.com/uploads/s1J6oxkt7uC9RBM1TrMUw2/ChatGPT_Image_8_jun_2026,_16_25_19.png', 'https://cdn.magicpatterns.com/uploads/kZUtDdMVZiGmtggygT7C3X/ChatGPT_Image_8_jun_2026,_16_27_11.png'];
export function Hero() {
  const {
    t
  } = useLanguage();
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, 400]);
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  const scrollTo = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const indicatorIcons = [Home, BedDouble, Car, Waves, Wind, ThermometerSun, DoorClosed, Zap];
  const indicators = indicatorIcons.map((icon, i) => ({
    icon,
    text: t.hero.indicators[i]
  }));
  return <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden" data-id="element-219" data-mp="2ljz1omw8l3t5">
      {/* Background with Parallax */}
      <motion.div style={{
      y
    }} className="absolute inset-0 z-0" data-id="element-220" data-mp="nat8vfsf4smt">
        <div className="absolute inset-0 bg-navy/40 z-10" data-id="element-221" data-mp="ibgn6yda9rlt" />{' '}
        {/* Dark overlay for text readability */}
        <AnimatePresence mode="sync" data-id="element-222" data-mp="de6rj0zawjpj">
          <motion.img key={current} src={heroImages[current]} alt="Residencial Aneto" initial={{
          opacity: 0,
          scale: 1.08
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          opacity: {
            duration: 1.4
          },
          scale: {
            duration: 6,
            ease: 'linear'
          }
        }} className="absolute inset-0 w-full h-[120%] object-cover object-center -mt-[10%]" data-id="element-223" data-mp="db2fjhflgjob" />
        </AnimatePresence>
      </motion.div>

      {/* Content */}
      <div className="container relative z-20 mx-auto px-6 md:px-12 text-center mt-20" data-id="element-224" data-mp="21a4ovi31ztzn">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-225" data-mp="269hgsh8s7r93">
          <h1 className="text-5xl md:text-7xl lg:text-8xl text-white font-serif mb-4 drop-shadow-lg tracking-tight" data-id="element-226" data-mp="2kyvntiovxwlb">
            {t.hero.title}
          </h1>
          <h2 className="text-2xl md:text-3xl lg:text-4xl text-white font-light mb-6 drop-shadow-md" data-id="element-227" data-mp="2p10f4arv2h4s">
            {t.hero.subtitle}
          </h2>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-10 font-light leading-relaxed" data-id="element-228" data-mp="umhfcfyperqg">
            {t.hero.desc}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-229" data-mp="zm2o1i3oeegd">
            <Button size="lg" variant="secondary" onClick={() => scrollTo('#disponibilidad')} data-id="element-230" data-mp="4a2s9dsxexwg">
              {t.hero.ctaPrimary}
            </Button>
            <Button size="lg" variant="outline" className="text-white border-white hover:bg-white hover:text-navy" onClick={() => scrollTo('#contacto')} data-id="element-231" data-mp="e91uadj6rvah">
              {t.hero.ctaSecondary}
            </Button>
          </div>
        </motion.div>

        {/* Indicators */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.6
      }} className="flex overflow-x-auto md:flex-wrap justify-start md:justify-center gap-x-6 md:gap-x-8 gap-y-4 lg:gap-x-12 bg-navy/80 backdrop-blur-sm py-6 md:py-8 px-6 md:px-10 rounded-2xl border border-white/10 max-w-6xl mx-auto hide-scrollbar snap-x snap-mandatory" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-232" data-mp="3rg3kzqmo8w5r">
          {indicators.map((item, index) => <div key={index} className="flex items-center gap-3 text-white flex-shrink-0 snap-start" data-id="element-233" data-mp="14jkd6g1gqir4" data-mpi={index}>
              <item.icon className="w-5 h-5 text-sand flex-shrink-0" data-id="element-234" data-mp="1sa6msbr48b38" />
              <span className="text-sm font-medium tracking-wide whitespace-nowrap" data-id="element-235" data-mp="1x7ztr708t94k">
                {item.text}
              </span>
            </div>)}
        </motion.div>
      </div>
    </section>;
}